// Angular Core
import {
  Component,
  computed,
  DestroyRef,
  inject,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';

// Angular Material
import { MatIcon } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTableModule, MatRow, MatTableDataSource } from '@angular/material/table';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDialog } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { provideNativeDateAdapter } from '@angular/material/core';

// Angular Common & Animations
import { DatePipe, CommonModule } from '@angular/common';
import {
  style,
  state,
  trigger,
  transition,
  animate
} from '@angular/animations';

// RxJS
import { forkJoin } from 'rxjs';

// CDK & Third Party
import { ScrollingModule } from '@angular/cdk/scrolling';
import { TableVirtualScrollModule } from 'ng-table-virtual-scroll';

// App Services & Interfaces
import { FeedstockService } from '../../services/feedstockservice';
import { DialogService } from '../../services/dialogservice';
import { LookUpService } from '../../services/lookupservice';
import { NotificationService } from '../../services/notificationservice';

// App Components
import { AddeditDeepackComponent } from './addedit-deepack/addedit-deepack.component';
import {
  ConfirmationDialogComponent,
  ConfirmationDialogData
} from '../shared/components/confirmation-dialog/confirmation-dialog.component';
import { AddEditRevenueComponent } from './add-edit-revenue/add-edit-revenue.component';
import { AttachDocumentsComponent } from './attach-documents/attach-documents.component';
import { ViewDocumentsComponent } from './view-documents/view-documents.component';

// App Models & Types
import {
  DeepackListRequest,
  DeepackListResponse,
  FeedStockLoadInfoResponse,
  FeedstockLoadScheduleResponse,
  GetDeepackOutboundRequest,
  GetDeepackOutboundResponse,
} from '../../interfaces/feedstocklistmodel';
import {
  Regions,
  RegionsInfo,
  SitesShortForm
} from '../../interfaces/regions';
import { FeedstockStatus } from '../../interfaces/feedstockstatus';
import { RoleService } from '../../services/role.service';
import { formatDateOnly } from '../shared/commonutils/helper';
import { AddeditOutboundComponent } from './addedit-outbound/addedit-outbound.component';
import { SelectSearchComponent } from '../shared/components/select-search/select-search.component';
import { MatSelectItems } from '../../interfaces/lookup';
import { DepackService } from '../../services/depack.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IdName } from '../../interfaces/pricematrixmodel';
import { AlertDialogComponent } from '../shared/components/alert-dialog/alert-dialog.component';

@Component({
  selector: 'app-deepack-loadschedule',
  standalone: true,
  imports: [MatIcon, MatTableModule, MatRow, MatPaginatorModule, MatSortModule, MatFormFieldModule,
    MatInputModule, MatButtonModule, MatSelectModule, MatDatepickerModule, DatePipe, CommonModule,
    MatMenuModule, MatProgressBarModule, MatProgressSpinnerModule, MatCardModule, FormsModule,
    ReactiveFormsModule, ScrollingModule, TableVirtualScrollModule, MatCheckboxModule,
    SelectSearchComponent],
  providers: [provideNativeDateAdapter()],
  templateUrl: './deepack.component.html',
  styleUrl: './deepack.component.scss',
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ]
})

export class DeepackComponent implements OnInit, OnDestroy {
  tempDocuments: { [docTypeId: number]: File[] } = {};
  markets: Regions[] = [];
  filteredMarkets: Regions[] = [];
  filteredStatus: FeedstockStatus[] = [];
  marketData: RegionsInfo[] = [];
  sites: SitesShortForm[] = [];
  siteId: number = 0;
  disposalSiteId: string | null = null;
  fsStatus: FeedstockStatus[] = [];
  marketId: number = -1;
  fsstatusId: number = 0;  
  direction: number | null = null;
  minDate = new Date(2000, 0, 1);
  searchText = '';
  range: FormGroup = new FormGroup({
    start: new FormControl<Date | null>(null),
    end: new FormControl<Date | null>(null),
  });
  tableVisible = true;
  selectSiteItems: MatSelectItems[] = [];
  selectMarketItems: MatSelectItems[] = [];
  selectStatusItems: MatSelectItems[] = [];
  directionData:MatSelectItems[]=[];
  baseColumns: string[] = [
    'actions',  'workOrderNumber', 'date','direction', 'status', 'loadType', 'destination', 'source', 'location', 'bol', 'seal',
    'purchaseOrderNo', 'driver','pickup', 'release', 'carrierName', 'weightConfirmationYN'
  ];
  displayedColumns = computed<string[]>(() => {
    var toRemove: string[] = [];
    if(this.userroles.includes('DRV')){
       toRemove = ['source'];
    }else{
      toRemove = ['destination'];
    }
    this.baseColumns = this.baseColumns.filter(item => !toRemove.includes(item));
    return this.baseColumns;
  });

  loadTypesOptions = [
    { value: 2, label: 'All' }
  ];
  
  selectedLoadDirection = '-1';
  selectedLoadType: number | null = 2;
  dataSource = new MatTableDataSource<any>;
  @ViewChild(MatSort, { static: true }) sort!: MatSort;
  
  userroles: string = "";
  private destroyRef = inject(DestroyRef);

  constructor(
    private fsService: FeedstockService,
    private depackService: DepackService,
    private dialog: MatDialog,
    private dialogService: DialogService,
    private lookUp: LookUpService,
    private notificationService: NotificationService,
    private route: ActivatedRoute,
    private rolesService: RoleService
  ) { }

  ngOnInit(): void {
    const today = new Date();
    const firstDayOfCurrentWeek = this.getFirstDayOfWeek(today);
    firstDayOfCurrentWeek.setDate(today.getDate() - 7);
    today.setDate(today.getDate() + 2);
    this.userroles = this.rolesService.getRolesAsString();

    const initialRange = new FormGroup({
      start: new FormControl<Date | null>(firstDayOfCurrentWeek),
      end: new FormControl<Date | null>(today)
    });
    this.range = initialRange;
    this.intialLoadDropdowns();
  }

  intialLoadDropdowns() {
    forkJoin({
      markets: this.lookUp.getMarketsList(""), //show all markets
      fsStatus: this.fsService.getFeedStockStatuses(''),//show the all statues
      sites: this.depackService.getDepackSites(),
      directions: this.depackService.getDirectionsList(),
      loadTypes: this.depackService.getLoadTypes()
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (response: { markets: Regions[], fsStatus: FeedstockStatus[], sites: SitesShortForm[], directions: IdName[], loadTypes: any }) => {

        this.markets = response.markets;
        this.fsStatus = response.fsStatus;
        this.filteredStatus = [...this.fsStatus];
        this.sites = response.sites;
        this.loadTypesOptions = response.loadTypes;

        this.selectSiteItems = this.sites.map(item => ({
          id: item.id!,
          name: item.siteName
        }));

        this.selectMarketItems = this.markets.map(item => ({
          id: item.id!,
          name: item.regionName!
        }));

        this.selectStatusItems = this.fsStatus.map(item => ({
          id: item.id!,
          name: item.description!
        }));

        this.directionData = response.directions.map(item => ({
          id: item.id,
          name: item.name
        }));

        this.onSearch();
      }
    });
  }

  onSearch() {    
    this.range.controls['start'].markAsDirty();
    this.range.controls['start'].markAsTouched();
    this.range.controls['end'].markAsDirty();
    this.range.controls['end'].markAsTouched();
    this.range.controls['start'].updateValueAndValidity();
    this.range.controls['end'].updateValueAndValidity();
    this.range.markAllAsTouched();
    this.range.updateValueAndValidity();
    
    if(this.range.invalid){
      this.dialog.open(AlertDialogComponent, {
        data: {
          title: 'Invalid Date Range',
          message: 'Please select a valid date range.'
        },
        disableClose: true,
        panelClass: 'delete-confirmation-dialog-nowrap'
      });
      return;
    }
    if (this.range.invalid || !this.range.value.start || !this.range.value.end) {
      this.dialog.open(AlertDialogComponent, {
        data: {
          title: 'Invalid Date Range',
          message: 'Please select a valid date range.'
        },
        disableClose: true,
        panelClass: 'delete-confirmation-dialog-nowrap'
      });
      return;
    }

    const fromDate = this.range.value.start ? formatDateOnly(this.range.value.start) : '';
    const toDate = this.range.value.end ? formatDateOnly(this.range.value.end) : '';

    var requestBody: GetDeepackOutboundRequest = {
      siteID: this.siteId == 0 ? '0' : `VRS-${this.siteId}`,
      fromDate: fromDate ?? '',
      toDate: toDate ?? '',
      status: this.fsstatusId,
      direction: (this.direction === -1 || this.direction === null) ? null : this.direction,
      loadType: this.selectedLoadType == 2 ? null : this.selectedLoadType,
    };
    this.depackService.getDepackOutboundData(requestBody).subscribe({
      next: (res: GetDeepackOutboundResponse[]) => {
        console.log("rep ", res);
        this.dataSource = new MatTableDataSource<any>(res);
        this.dataSource.sort = this.sort;
        if (this.searchText) {
          this.dataSource.filter = this.searchText.trim().toLowerCase();
        }
        console.log("outbound and inbound loads", res);
      }
    });
  }

  loadMenuDataAndOpen(row: FeedstockLoadScheduleResponse, trigger: MatMenuTrigger): void {
    this.fsService.getFeedStockLoadInfoById(row.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: FeedStockLoadInfoResponse) => {
          row.menuData = data;
          row.menuData.sampleStatus = row.sampleStatus;
          trigger.openMenu();
        }
      });
  }

  applyFilter(event: Event) {
    this.searchText = (event.target as HTMLInputElement).value;
    this.dataSource.filter = this.searchText.trim().toLowerCase();

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  addRevenue(data: any, workOrderNumber: string, status: string) {
    const dialogRef = this.dialog.open(AddEditRevenueComponent, {
      panelClass: 'big-dialog',
      maxWidth: '780px',
      disableClose: true,
      data: {
        task: 'Add',
        fsLoadId: data,
        workOrderNumber: workOrderNumber,
        fsStatus: status
      }
    });
    dialogRef.afterClosed().subscribe((result: any) => {
    });
  }

  onEditOutboundOrInbound(fsId: any, taskType: any, direction: boolean, row: GetDeepackOutboundResponse | null){
    if(!direction){
      this.CreateInboundDialog(fsId, taskType, row);
    }
    else{
      this.CreateouboundDialog(fsId, taskType, row);
    }
  }

  openEditDeepakDialog(direction: boolean, fsId: any, mode: any, row: GetDeepackOutboundResponse| null){
    if(!direction){
      this.onOpenLoadDialog(fsId, mode);
    }
    else{
      this.CreateouboundDialog(fsId, mode, row);
    }
  }

  CreateouboundDialog(fsId: any, taskType: any, row: GetDeepackOutboundResponse| null) {
    
    let driverMode = false;
    if(this.userroles.includes("DRV")){
      driverMode = true;
    }
    const dialogRef = this.dialog.open(AddeditOutboundComponent, {
      maxWidth: '950px',
      panelClass: 'big-dialog',
      disableClose: true,
      data: {
        task: taskType,
        type: "Outbound",
        driverMode: driverMode,
        ipData: row
      }
    });
    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        this.notificationService.show("Outbound Data saved successfully", 'Success');
        this.onSearch();
      }
    });
  }
  
  CreateInboundDialog(fsId: any, taskType: any, row: GetDeepackOutboundResponse | null) {
    let driverMode = false;
    if(this.userroles.includes("DRV")){
      driverMode = true;
    }
    const dialogRef = this.dialog.open(AddeditOutboundComponent, {
      maxWidth: '950px',
      maxHeight: '90vh',
      disableClose: true,
      data: {
        task: taskType,
        type: "Inbound",
        driverMode: driverMode,
        ipData: row
      }
    });
    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        this.notificationService.show("Inbound Data saved successfully", 'Success');
        this.onSearch();
      }
    });
  }

  onOpenLoadDialog(fsId: any, mode: any) {
    localStorage.removeItem('documents');
    const dialogRef = this.dialog.open(AddeditDeepackComponent, {
      width: '94vw',
      maxWidth: '94vw',
      disableClose: true,
      data: {
        mode: mode,
        fsLoadId: fsId,
        marketId: this.marketId
      }
    });
    dialogRef.afterClosed().subscribe((result: any) => {
      if (result) {
        this.notificationService.show("Inbound Data saved successfully", 'Success');
        this.onSearch();
      }
    });
  }

  getIconBgClass(row: any): any {
    return { iconBGColor: row.menuData?.revenueRecord > 0 };
  }

  onDeleteLoad(fsid: number, workOrderNumber: string) {
    const dialogData: ConfirmationDialogData = {
      title: 'Confirm Deletion',
      message: `Are you sure you want to delete Load ${workOrderNumber}?`,
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel',
      confirmButtonColor: 'warn'
    };

    this.dialog.open(ConfirmationDialogComponent, {
      width: '420px',
      panelClass: 'delete-confirmation-dialog-nowrap',
      data: dialogData,
      disableClose: true
    }).afterClosed().subscribe(result => {
      if (result) {
        if (fsid !== null && fsid !== undefined) {
          this.fsService.deleteFeedstockloadscheduleById(fsid).subscribe({
            next: () => {
              this.dataSource.data = this.dataSource.data.filter(item => item.id !== fsid);
              this.notificationService.show("Data deleted successfully", 'Success');
            }
          });
        }
      }
    });
  }

  getFirstDayOfWeek(date: Date) {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day;
    d.setDate(diff);
    return d;
  }

  openAttachDocuments(id: number, workOrderNumber: string): void {
    localStorage.removeItem('documents');
    const dialogRef = this.dialog.open(AttachDocumentsComponent, {
      panelClass: 'attachDocumentsDialog',
      disableClose: true,
      data: {
        loadScheduleId: id,
        screenTypeId: '0',
        name: workOrderNumber,
        existingDocuments: this.tempDocuments,
        disableAddMoreFiles: true
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.tempDocuments = result;
        this.onSearch();
      }
    });
  }

  openViewDocsDialog(loadScheduleId: number, workOrderNumber: string, row: GetDeepackOutboundResponse): void {
    const dialogRef = this.dialog.open(ViewDocumentsComponent, {
      //panelClass: 'viewDocsDialog',
      disableClose: true,
      data: {
        loadScheduleId: loadScheduleId,
        screenTypeId: '0',
        name: workOrderNumber
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        row.loadSlip = false;
  
        this.dataSource.data = this.dataSource.data.map(x =>
          x.id === row.id ? { ...x, loadSlip: false } : x
        );
      }
    });
  }

  onSiteReceived(event: number | null) {
    this.siteId = event ?? 0;
    this.disposalSiteId = event == null ? '' : "VRS-".concat(event.toString());
  }

  onMarketReceived(event: number) {
    this.marketId = event;
  }

  onStatusReceived(event: number | null) {
    this.fsstatusId = event ?? 0;
  }

  onDirectionReceived(event: number | null){    
    this.direction = event;
  }

  ngOnDestroy(): void {

  }

  onKeydown(event: KeyboardEvent) {
    if(event.ctrlKey && event.metaKey){return;} 
    if(!/[0-9\/]/.test(event.key) && event.key.length === 1){
      event.preventDefault();
    }
    setTimeout(() => {
      this.range.updateValueAndValidity();
      this.range.markAllAsTouched();
    });
  }

  dateformat(event: Event){
    const input = event.target as HTMLInputElement;
    let value = input.value;

    if(!value) return;
    const parts = value.split('/');
    if(parts.length !== 3) return;

    let [month, day, year] = parts.map(part => part.padStart(2, '0'));
    if(month.length === 1)  month = '0' + month;
    if(day.length === 1)  day = '0' + day;
    if(year.length === 2) year = '20' + year;
    input.value = `${month}/${day}/${year}`;
  }
}
